import React from "react";
import { Spinner, SpinnerSize } from "@fluentui/react";
import { useTheme } from "../theme/OAS365Theme";

/**
 * @typedef {Object} CustomSpinnerStyles
 * @property {Object} [root] - Styles applied to spinner root container
 * @property {Object} [circle] - Styles applied to the spinner circle
 * @property {Object} [label] - Styles applied to the label element
 *
 * @typedef {Object} CustomSpinnerProps
 * @property {import('@fluentui/react').SpinnerSize} [size] - Fluent UI spinner size
 * @property {string} [thickness] - Border thickness for the spinner ring (e.g., '3px')
 * @property {string|number} [diameter] - Explicit diameter for spinner (e.g., '20px' or 20)
 * @property {string} [label] - Optional label to show with the spinner
 * @property {'bottom'|'right'|'top'} [labelPosition] - Label position relative to the spinner
 * @property {string} [color] - Accent color for the active ring segment
 * @property {CustomSpinnerStyles} [styles] - Style overrides for root/circle/label
 */

/**
 * @param {CustomSpinnerProps} props
 */
const CustomSpinner = ({
  size = SpinnerSize.medium,
  thickness = "3px",
  diameter,
  label,
  labelPosition = "bottom",
  color,
  styles: customStyles = {},
}) => {
  const { colors } = useTheme();
  const accent = color || colors.addovation50Green;
  const accent2 = colors.addovation20Green;

  const spinnerStyles = {
    circle: {
      borderWidth: thickness,
      borderColor: diameter ? accent : `${accent} ${accent2} ${accent2} ${accent2}`,
      ...(diameter && { width: diameter, height: diameter }),
    },
    ...(diameter && { root: { width: diameter, height: diameter } }),
    label: {
      fontFamily: "Poppins, sans-serif",
      WebkitFontSmoothing: "antialiased",
      fontSize: "12px",
      fontWeight: 400,
      color: "rgb(113, 173, 141)",
      margin: labelPosition === "right" ? "0 0 0 8px" : "8px 0px 0px",
      textAlign: labelPosition === "right" ? "left" : "center",
    },
    ...customStyles,
  };

  if (labelPosition === "right" && label) {
    return (
      <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
        <Spinner size={size} styles={spinnerStyles} />
        <span
          style={{
            fontFamily: "Poppins, sans-serif",
            fontSize: "12px",
            fontWeight: 400,
            color: "rgb(113, 173, 141)",
          }}
        >
          {label}
        </span>
      </div>
    );
  }

  return <Spinner size={size} styles={spinnerStyles} label={label} />;
};

export default CustomSpinner;
